import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { parseDatasetJson, exportDatasetJson } from "../../../export/json.ts";
import { listSessionBackups, readSessionBackup } from "../../../store/backups.ts";
import type { RetainedBackup } from "../../../store/backupRetention.ts";
import { store } from "../../../store/index.ts";
import { downloadBlob } from "../../download.ts";
import { Section } from "../../components/Section.tsx";

function filename(backup: RetainedBackup): string {
  return `budget-backup-${backup.createdAt.replaceAll(":", "-")}.json`;
}

function describeError(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function BackupSection() {
  const [backups, setBackups] = useState<RetainedBackup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<RetainedBackup | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    listSessionBackups().then(
      (items) => { if (active) setBackups(items); },
      (cause) => { if (active) setError(`Could not read backups: ${describeError(cause)}`); },
    );
    return () => { active = false; };
  }, []);

  async function download(backup: RetainedBackup) {
    try {
      setError(null);
      const record = await readSessionBackup(backup.id);
      downloadBlob(filename(backup), new Blob([record.json], { type: "application/json" }));
    } catch (cause) {
      setError(`Could not download backup: ${describeError(cause)}`);
    }
  }

  async function confirmRestore() {
    if (!restoring || busy) return;
    setBusy(true);
    setError(null);
    try {
      const record = await readSessionBackup(restoring.id);
      const restored = parseDatasetJson(record.json);
      // Same rule as JSON import and Reset: download the active dataset before
      // a whole-dataset write. If this fails, leave the active data intact.
      downloadBlob(
        `budget-before-restore-${new Date().toISOString().replaceAll(":", "-")}.json`,
        new Blob([exportDatasetJson(store.get())], { type: "application/json" }),
      );
      await store.replace(restored);
      setRestoring(null);
    } catch (cause) {
      setError(`Could not restore backup: ${describeError(cause)}. Your active data was not changed.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Session backups"
      hint="Saved in this browser when Budget opens. The newest backup from each of 7 days, 8 weeks and 12 months is kept. Export JSON too: browser storage can be lost together."
    >
      {error && <p role="alert" className="mb-3 text-sm text-destructive">{error}</p>}
      {backups === null ? (
        <p className="text-sm text-budget-ink-muted">Loading backups…</p>
      ) : backups.length === 0 ? (
        <p className="text-sm text-budget-ink-muted">No session backups yet.</p>
      ) : (
        <ul className="divide-y divide-budget-rule">
          {backups.map((backup) => (
            <li key={backup.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <span>
                <span className="font-medium">{new Date(backup.createdAt).toLocaleString()}</span>
                <span className="ml-2 text-xs capitalize text-budget-ink-muted">{backup.tier}</span>
              </span>
              <span className="flex gap-1">
                <Button size="xs" variant="ghost" onClick={() => void download(backup)}>Download</Button>
                <Button size="xs" variant="outline" onClick={() => { setError(null); setRestoring(backup); }}>Restore…</Button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {restoring && (
        <div className="mt-4 space-y-3 rounded-md border border-destructive/60 bg-destructive/5 p-4 text-sm">
          <p>Restore the backup from {new Date(restoring.createdAt).toLocaleString()}? It will replace your active budget.</p>
          <p className="text-xs text-budget-ink-muted">A JSON copy of your current budget will download first.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" disabled={busy} onClick={() => void confirmRestore()}>Restore backup</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setRestoring(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </Section>
  );
}
